// BooksTradeOrders — the "Books sales orders" block on the Admin page's Google Calendar card (Oct 29, 2026).
//
// Greg: "lets send a sales order to books for the trade to confirm the invoice matches the sales order pricing and
// measurements before paying the invoice". Nothing goes to Books until it is turned on here (finance_settings
// books_trade_orders_enabled, off by default): once Books migration 0095 and its ingest-event are live, the admin tier
// clicks "Turn on and send existing jobs" (books_trade_orders_enable: the flag, then every priced JAC job once). The
// block shows how many orders Books has (counts only, no money) and the last pull of what Books says back.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";

interface OrdersStatus {
  enabled: boolean;
  orders: { total: number; in_books: number; waiting: number; failing: number; unsent: number } | null;
  by_status: Record<string, number> | null;
  last_send_error: string | null;
  pull: {
    last_run_at: string | null; last_sent_at: string | null; last_applied_at: string | null;
    last_error: string | null; last_seq: number | null; pending: number | null;
  } | null;
  last_notice_at: string | null;
  /** Notices from Books this CRM could not apply in the last 14 days (rolled back; an adjustment's also told Chad), the last five reasons. */
  notices_failed?: number;
  last_failures?: Array<{ seq: number; kind: string | null; outcome: string; at: string }>;
}

interface EnableResult {
  enabled: boolean;
  backfill: { jobs?: number; sent?: number; same?: number; held?: number; requests?: number; note?: string } | null;
}

const STATUS_WORDS: Array<[string, string]> = [
  ["open", "waiting on JAC's invoice"], ["billed", "invoice matches"], ["differs", "invoice differs"],
  ["adjusted", "adjusted in Books"], ["paid", "paid"], ["voided", "withdrawn"],
];

function fmtWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const NOTICE_WORDS: Record<string, string> = {
  order_billed: "JAC's invoice", order_adjusted: "Shannon's adjustment", order_paid: "a payment",
  order_payment_voided: "a payment taken back", order_unlinked: "an unlinked invoice",
};

export default function BooksTradeOrders() {
  const { role, isSuperUser } = useAuth();
  // ⚠ Mirror of books_trade_orders_enable(): the admin tier (super users resolve to admin there).
  const canEnable = isSuperUser || role === "admin";
  const [status, setStatus] = useState<OrdersStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The status is for the price viewers (books_trade_orders_status: trade_can_see_prices, so not Jeremy): Books' error
  // text can carry an amount.
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("books_trade_orders_status");
    if (error) {
      if (error.code === "42501") { setHidden(true); setErr(null); return; }
      setErr(error.message);
      return;
    }
    setHidden(false);
    setErr(null);
    setStatus(data as OrdersStatus);
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function enable() {
    const again = !!status?.enabled;
    if (!window.confirm(again
      ? "Send Books every priced JAC job it doesn't have yet? Jobs Books already has are skipped."
      : "Turn on the Books sales orders? Every priced JAC job goes to Books as an order now, and every change after. Books migration 0095 and its ingest-event must be live first.")) return;
    setBusy(true);
    setBanner(null);
    try {
      const { data, error } = await supabase.rpc("books_trade_orders_enable");
      if (error) throw new Error(error.message);
      const b = (data as EnableResult | null)?.backfill ?? null;
      const sent = b?.sent ?? 0;
      const parts = [
        `${plural(sent, "job", "jobs")} sent to Books${b?.requests ? ` in ${plural(b.requests, "request", "requests")}` : ""}`,
        b?.same ? `${b.same} already there` : null,
        b?.held ? `${b.held} waiting on JAC's quote` : null,
      ].filter(Boolean);
      setBanner({ kind: "ok", text: `${again ? "" : "On. "}${parts.join(", ")}.${b?.note ? ` ${b.note}` : ""}` });
      await load();
    } catch (e) {
      setBanner({ kind: "err", text: (e as Error).message });
    }
    setBusy(false);
  }

  const o = status?.orders ?? null;
  const pull = status?.pull ?? null;
  const counts = STATUS_WORDS.map(([k, w]) => [w, status?.by_status?.[k] ?? 0] as const).filter(([, n]) => n > 0);

  return (
    <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-slate-700">
          <strong>Books sales orders</strong>: each priced JAC job goes to Books as an order, and Books checks JAC's invoice against it (price and footage) before it's paid. Shannon adjusts the order there; Chad is told.
        </div>
        {status && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${status.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {status.enabled ? "On" : "Off"}
          </span>
        )}
      </div>

      {banner && (
        <div className={`rounded-lg px-3 py-2 text-sm ${banner.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>{banner.text}</div>
      )}
      {err && <div className="text-xs text-rose-700">Couldn't read the Books sales orders: {err}</div>}
      {hidden && <div className="text-xs text-slate-500">Where the orders stand shows to the people who see trade prices.</div>}

      {status && (
        <div className="text-xs text-slate-600 space-y-0.5">
          {o && (
            <div>
              {plural(o.in_books, "job", "jobs")} in Books
              {o.waiting > 0 && ` · ${o.waiting} waiting on Books to confirm`}
              {o.failing > 0 && <span className="text-rose-700"> · {o.failing} refused or unanswered</span>}
              {o.unsent > 0 && ` · ${o.unsent} not sent yet`}
            </div>
          )}
          {counts.length > 0 && <div>{counts.map(([w, n]) => `${n} ${w}`).join(" · ")}</div>}
          <div>
            Last pull from Books: {fmtWhen(pull?.last_run_at) ?? "never"}
            {fmtWhen(pull?.last_applied_at) && ` · last news applied ${fmtWhen(pull?.last_applied_at)}`}
            {(pull?.pending ?? 0) > 0 && ` · ${pull?.pending} more waiting in Books`}
          </div>
          {pull?.last_error && <div className="text-rose-700">Last pull error: {pull.last_error}</div>}
          {status.last_send_error && <div className="text-rose-700">Last send error: {status.last_send_error}</div>}
          {(status.notices_failed ?? 0) > 0 && (
            <div className="text-rose-700">
              {plural(status.notices_failed ?? 0, "notice", "notices")} from Books couldn't be applied here in the last 14 days (nothing changed for {status.notices_failed === 1 ? "it" : "them"}):
              <ul className="list-disc pl-4">
                {(status.last_failures ?? []).map((f) => (
                  <li key={f.seq}>
                    {NOTICE_WORDS[f.kind ?? ""] ?? f.kind ?? "a notice"} · {fmtWhen(f.at)} · {f.outcome.replace(/^not applied:\s*/, "")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {canEnable ? (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => void enable()} disabled={busy || !status}
                  className="px-3 py-1.5 rounded bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
            {busy ? "Sending…" : status?.enabled ? "Send any job Books doesn't have" : "Turn on and send existing jobs"}
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-slate-500">The admin tier turns it on.</p>
      )}
      <p className="text-[11px] text-slate-500">
        Turn it on once Books migration 0095 and its ingest-event are live: until then Books doesn't know the orders and turns each one away (it is tried 5 times).
        To turn it off: finance_settings books_trade_orders_enabled = false.
      </p>
    </div>
  );
}
